import { useState, useEffect } from 'react';
import { HoldingsPlanLineItem, HoldingsSubscription, HoldingsBillingCycle, getMonthlyEquivalent } from '../../types';
import { Button, Input, Select, Textarea } from "@repo/o37-ui";
import { X } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

const LINE_ITEM_TYPES = [
  { value: 'new_subscription', label: 'New Subscription' },
  { value: 'modify_subscription', label: 'Modify Subscription' },
  { value: 'cancel_subscription', label: 'Cancel Subscription' },
  { value: 'headcount_change', label: 'Headcount Change' },
  { value: 'one_time_expense', label: 'One-Time Expense' }
];

const BILLING_CYCLES: { value: HoldingsBillingCycle; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
  { value: 'one_time', label: 'One-Time' }
];

interface PlanLineItemFormProps {
  planId: string;
  lineItem?: HoldingsPlanLineItem | null;
  subscriptions: HoldingsSubscription[];
  onClose: () => void;
  onSaved: () => void;
}

export default function PlanLineItemForm({
  planId,
  lineItem,
  subscriptions,
  onClose,
  onSaved
}: PlanLineItemFormProps) {
  const isEditing = !!lineItem;

  const [itemType, setItemType] = useState(lineItem?.itemType || 'new_subscription');
  const [name, setName] = useState(lineItem?.name || '');
  const [description, setDescription] = useState(lineItem?.description || '');
  const [subscriptionId, setSubscriptionId] = useState(lineItem?.subscriptionId || '');
  const [newCostCents, setNewCostCents] = useState(lineItem?.newCostCents ? lineItem.newCostCents / 100 : 0);
  const [newBillingCycle, setNewBillingCycle] = useState<HoldingsBillingCycle>(lineItem?.newBillingCycle || 'monthly');
  const [seatChange, setSeatChange] = useState(lineItem?.seatChange || 0);
  const [personName, setPersonName] = useState(lineItem?.personName || '');
  const [role, setRole] = useState(lineItem?.role || '');
  const [salaryCents, setSalaryCents] = useState(lineItem?.salaryCents ? lineItem.salaryCents / 100 : 0);
  const [headcountDelta, setHeadcountDelta] = useState(lineItem?.headcountDelta || 1);
  const [expenseCategory, setExpenseCategory] = useState(lineItem?.expenseCategory || '');
  const [effectiveDate, setEffectiveDate] = useState(
    lineItem?.effectiveDate || new Date().toISOString().split('T')[0]
  );
  const [notes, setNotes] = useState(lineItem?.notes || '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  // Auto-fill name when selecting subscription
  useEffect(() => {
    if (subscriptionId && !name) {
      const sub = subscriptions.find(s => s.id === subscriptionId);
      if (sub) {
        if (itemType === 'cancel_subscription') {
          setName(`Cancel ${sub.vendorName}`);
        } else if (itemType === 'modify_subscription') {
          setName(`Modify ${sub.vendorName}`);
        }
      }
    }
  }, [subscriptionId, itemType, subscriptions, name]);

  const calculateImpacts = () => {
    let monthlyImpact = 0;
    let annualImpact = 0;

    if (itemType === 'new_subscription' || itemType === 'modify_subscription') {
      const costCents = Math.round(newCostCents * 100);
      monthlyImpact = getMonthlyEquivalent(costCents, newBillingCycle);

      // For modifications, subtract the current cost
      if (itemType === 'modify_subscription' && subscriptionId) {
        const currentSub = subscriptions.find(s => s.id === subscriptionId);
        if (currentSub) {
          const currentMonthly = getMonthlyEquivalent(currentSub.costCents, currentSub.billingCycle);
          monthlyImpact = monthlyImpact - currentMonthly;
        }
      }

      annualImpact = monthlyImpact * 12;
    } else if (itemType === 'cancel_subscription' && subscriptionId) {
      const sub = subscriptions.find(s => s.id === subscriptionId);
      if (sub) {
        monthlyImpact = -getMonthlyEquivalent(sub.costCents, sub.billingCycle);
        annualImpact = monthlyImpact * 12;
      }
    } else if (itemType === 'headcount_change') {
      const monthlySalary = Math.round(salaryCents * 100) / 12;
      monthlyImpact = monthlySalary * headcountDelta;
      annualImpact = Math.round(salaryCents * 100) * headcountDelta;
    } else if (itemType === 'one_time_expense') {
      monthlyImpact = 0;
      annualImpact = Math.round(newCostCents * 100);
    }

    return {
      monthlyImpactCents: Math.round(monthlyImpact),
      annualImpactCents: Math.round(annualImpact)
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!effectiveDate) {
      setError('Effective date is required');
      return;
    }

    // Validate based on item type
    if ((itemType === 'modify_subscription' || itemType === 'cancel_subscription') && !subscriptionId) {
      setError('Please select a subscription');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const impacts = calculateImpacts();

      const payload = {
        itemType,
        name: name.trim(),
        description: description.trim() || null,
        subscriptionId: subscriptionId || null,
        newCostCents: (itemType === 'new_subscription' || itemType === 'modify_subscription' || itemType === 'one_time_expense')
          ? Math.round(newCostCents * 100)
          : null,
        newBillingCycle: (itemType === 'new_subscription' || itemType === 'modify_subscription')
          ? newBillingCycle
          : null,
        seatChange: (itemType === 'new_subscription' || itemType === 'modify_subscription') ? seatChange : null,
        personName: itemType === 'headcount_change' ? personName.trim() || null : null,
        role: itemType === 'headcount_change' ? role.trim() || null : null,
        salaryCents: itemType === 'headcount_change' ? Math.round(salaryCents * 100) : null,
        headcountDelta: itemType === 'headcount_change' ? headcountDelta : null,
        expenseCategory: itemType === 'one_time_expense' ? expenseCategory.trim() || null : null,
        effectiveDate,
        monthlyImpactCents: impacts.monthlyImpactCents,
        annualImpactCents: impacts.annualImpactCents,
        notes: notes.trim() || null
      };

      const url = isEditing
        ? `${API_URL}/holdings/plans/${planId}/line-items/${lineItem.id}`
        : `${API_URL}/holdings/plans/${planId}/line-items`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save line item');
      }
    } catch (e) {
      setError('Failed to save line item');
    } finally {
      setSaving(false);
    }
  };

  const impacts = calculateImpacts();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100 sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Line Item' : 'Add Line Item'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Item Type */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Type *
            </label>
            <Select value={itemType} onChange={(e) => setItemType(e.target.value as any)}>
              {LINE_ITEM_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Add Notion subscription"
              required
            />
          </div>

          {/* Subscription Selection (for modify/cancel) */}
          {(itemType === 'modify_subscription' || itemType === 'cancel_subscription') && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Select Subscription *
              </label>
              <Select value={subscriptionId} onChange={(e) => setSubscriptionId(e.target.value)}>
                <option value="">Select subscription...</option>
                {subscriptions
                  .filter(s => s.status === 'active')
                  .map(s => (
                    <option key={s.id} value={s.id}>{s.vendorName}</option>
                  ))}
              </Select>
            </div>
          )}

          {/* Cost & Billing (for new/modify subscription) */}
          {(itemType === 'new_subscription' || itemType === 'modify_subscription') && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    {itemType === 'modify_subscription' ? 'New Cost' : 'Cost'} *
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newCostCents}
                    onChange={(e) => setNewCostCents(parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Billing Cycle
                  </label>
                  <Select value={newBillingCycle} onChange={(e) => setNewBillingCycle(e.target.value as HoldingsBillingCycle)}>
                    {BILLING_CYCLES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Seat Change
                </label>
                <Input
                  type="number"
                  value={seatChange}
                  onChange={(e) => setSeatChange(parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Positive for adding seats, negative for removing
                </p>
              </div>
            </>
          )}

          {/* One-Time Expense */}
          {itemType === 'one_time_expense' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Amount *
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newCostCents}
                    onChange={(e) => setNewCostCents(parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Category
                  </label>
                  <Input
                    value={expenseCategory}
                    onChange={(e) => setExpenseCategory(e.target.value)}
                    placeholder="e.g., Equipment"
                  />
                </div>
              </div>
            </>
          )}

          {/* Headcount Change */}
          {itemType === 'headcount_change' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Person Name
                  </label>
                  <Input
                    value={personName}
                    onChange={(e) => setPersonName(e.target.value)}
                    placeholder="e.g., John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Role
                  </label>
                  <Input
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g., Engineer"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Annual Salary
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={salaryCents}
                    onChange={(e) => setSalaryCents(parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Headcount Change
                  </label>
                  <Input
                    type="number"
                    value={headcountDelta}
                    onChange={(e) => setHeadcountDelta(parseInt(e.target.value) || 0)}
                    placeholder="1"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Positive for hiring, negative for departures
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Effective Date */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Effective Date *
            </label>
            <Input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Description
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>

          {/* Impact Preview */}
          <div className="p-4 bg-neutral-50 rounded-lg">
            <h4 className="text-sm font-medium text-neutral-700 mb-2">Impact Preview</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-neutral-500">Monthly:</span>
                <span className={`ml-2 font-medium ${
                  impacts.monthlyImpactCents > 0 ? 'text-red-600' : impacts.monthlyImpactCents < 0 ? 'text-emerald-600' : 'text-neutral-900'
                }`}>
                  {impacts.monthlyImpactCents > 0 ? '+' : ''}${(impacts.monthlyImpactCents / 100).toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-neutral-500">Annual:</span>
                <span className={`ml-2 font-medium ${
                  impacts.annualImpactCents > 0 ? 'text-red-600' : impacts.annualImpactCents < 0 ? 'text-emerald-600' : 'text-neutral-900'
                }`}>
                  {impacts.annualImpactCents > 0 ? '+' : ''}${(impacts.annualImpactCents / 100).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} text="Cancel" />
            <Button type="submit" disabled={saving} text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Item'} />
          </div>
        </form>
      </div>
    </div>
  );
}
